import { setupDeleteConfirmation } from '../lib/delete-confirmation';
import { setupLoadMorePrices } from '../modules/offer/load-more-prices';
import { initUnitModal } from './offer-batch-modals';
import { init as initFolderRail } from './offer-folder-rail';
import { init as initBulkPrices } from './offer-bulk-prices';
import { init as initInlineEdit } from './offer-inline-edit';
import { init as initModalEdit } from './offer-modal-edit';
import { renderAiUsageBadge, readLabelsFromContainer, AiUsage } from '../modules/ai/ai-usage-badge';
import { getAntiForgeryToken } from '../lib/helpers';

document.addEventListener('DOMContentLoaded', () => {
    // Delete confirmation
    setupDeleteConfirmation('.form-delete-offer', {
        title: 'Delete Offer',
        text: 'Are you sure you want to delete this offer? This action cannot be undone.'
    });

    // Load more prices
    setupLoadMorePrices();

    // Batch modals
    const indexContainer = document.getElementById('offer-index-container') as HTMLElement | null;
    if (!indexContainer) return;

    const sharedConfig = {
        urlCreateUnits: indexContainer.dataset.urlCreateUnits || '',
        urlUnitFormData: indexContainer.dataset.urlUnitFormData || '',
    };

    initUnitModal(sharedConfig);
    initFolderRail();
    initBulkPrices();
    initInlineEdit();
    initModalEdit();

    // AI search toggle
    initAiSearch(indexContainer);
});

interface RankedOfferDTO {
    offerId: number;
    score: number;
    rationale: string;
}

interface AiSearchChatResult {
    sessionId: number;
    assistantMessage: string;
    rankedOffers: RankedOfferDTO[];
    suggestedFollowUps: string[];
}

function initAiSearch(container: HTMLElement) {
    const toggleBtn = document.getElementById('ai-search-toggle');
    const searchForm = document.getElementById('offer-search-form');
    const aiPanel = document.getElementById('ai-search-panel');
    const messagesEl = document.getElementById('ai-search-messages');
    const aiInput = document.getElementById('ai-search-input') as HTMLInputElement | null;
    const sendBtn = document.getElementById('ai-search-send');

    if (!toggleBtn || !aiPanel) return;
    const urlAiSearch = aiPanel.dataset.urlAiSearch || '';
    const agencyId = aiPanel.dataset.agencyId || '0';
    const labels = readLabelsFromContainer(aiPanel);
    const locThinking = aiPanel.dataset.locThinking ?? 'Thinking...';
    const locFailed = aiPanel.dataset.locFailed ?? 'AI search failed.';
    const locNoResults = aiPanel.dataset.locNoResults ?? 'No relevant offers found.';

    let sessionId: number | null = null;
    let aiActive = false;

    toggleBtn.addEventListener('click', () => {
        aiActive = !aiActive;
        aiPanel.classList.toggle('d-none', !aiActive);
        searchForm?.classList.toggle('d-none', aiActive);
        if (aiActive) aiInput?.focus();
    });

    if (!aiInput || !sendBtn || !messagesEl || !urlAiSearch) return;

    sendBtn.addEventListener('click', () => sendMessage());
    aiInput.addEventListener('keydown', e => { if (e.key === 'Enter') sendMessage(); });

    async function sendMessage() {
        const msg = aiInput!.value.trim();
        if (!msg) return;

        appendBubble('user', msg);
        aiInput!.value = '';
        sendBtn!.setAttribute('disabled', '');

        const thinking = appendBubble('assistant', locThinking);

        try {
            const token = getAntiForgeryToken();
            const resp = await fetch(`${urlAiSearch}?agencyId=${agencyId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'RequestVerificationToken': token },
                body: JSON.stringify({ sessionId, message: msg })
            });
            const json = await resp.json();
            thinking.remove();

            if (!json.success) {
                appendBubble('assistant', json.message ?? locFailed);
                return;
            }

            const data: AiSearchChatResult = json.data;
            sessionId = data.sessionId;

            const bubbleEl = appendBubble('assistant', data.assistantMessage || '');

            if (data.rankedOffers?.length) {
                highlightOfferCards(data.rankedOffers);
            }

            if (json.usage) {
                bubbleEl.insertAdjacentHTML('afterend', renderAiUsageBadge(json.usage as AiUsage, labels));
            }

            if (data.suggestedFollowUps?.length) {
                const chipsRow = document.createElement('div');
                chipsRow.className = 'd-flex flex-wrap gap-1 mt-1';
                data.suggestedFollowUps.forEach(prompt => {
                    const chip = document.createElement('button');
                    chip.type = 'button';
                    chip.className = 'btn btn-sm btn-outline-secondary';
                    chip.textContent = prompt;
                    chip.addEventListener('click', () => {
                        aiInput!.value = prompt;
                        sendMessage();
                    });
                    chipsRow.appendChild(chip);
                });
                bubbleEl.parentElement!.insertAdjacentElement('afterend', chipsRow);
            }
        } catch {
            thinking.remove();
            appendBubble('assistant', locFailed);
        } finally {
            sendBtn!.removeAttribute('disabled');
        }
    }

    function appendBubble(role: 'user' | 'assistant', text: string): HTMLElement {
        const isUser = role === 'user';
        const row = document.createElement('div');
        row.className = `d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} mb-2`;
        const bubble = document.createElement('div');
        bubble.className = `p-2 rounded small ${isUser ? 'bg-primary text-white' : 'bg-light text-dark'}`;
        bubble.style.maxWidth = '85%';
        bubble.style.whiteSpace = 'pre-wrap';
        bubble.textContent = text;
        row.appendChild(bubble);
        messagesEl!.appendChild(row);
        messagesEl!.scrollTop = messagesEl!.scrollHeight;
        return bubble;
    }

    function highlightOfferCards(ranked: RankedOfferDTO[]) {
        const matchedIds = new Set(ranked.map(r => r.offerId));
        document.querySelectorAll<HTMLElement>('.offer-card').forEach(card => {
            const id = parseInt(card.dataset.offerId || '0');
            if (!matchedIds.has(id)) {
                card.style.opacity = '0.4';
            } else {
                card.style.opacity = '';
                const r = ranked.find(x => x.offerId === id);
                if (r) {
                    let badge = card.querySelector('.ai-rationale') as HTMLElement | null;
                    if (!badge) {
                        badge = document.createElement('div');
                        badge.className = 'ai-rationale text-muted small px-3 pb-2';
                        card.appendChild(badge);
                    }
                    badge.textContent = `✨ ${r.rationale}`;
                }
            }
        });
    }
}
